import * as Keychain from 'react-native-keychain';
import auth, { FirebaseAuthTypes } from '@react-native-firebase/auth';
import { setAuthToken } from './api';
import { v2Login, v2GetMe } from './v2/api';
import type { V2LoginResponse, V2User } from '../types/v2';
import analyticsService from './analytics';
import * as biometrics from './biometrics';

const KEYCHAIN_SERVICE = 'one-app-auth';
const FIRST_LOGIN_FLAG_SERVICE = 'one-app-first-login';
const TERMS_ACCEPTED_SERVICE = 'one-app-terms-accepted';

export interface AuthCredentials {
  login: string;
  accessToken: string;
}

export interface AuthResult {
  user: V2User;
  firebaseUser: FirebaseAuthTypes.User | null;
}

/**
 * Store credentials securely in Keychain
 */
export const storeCredentials = async (
  login: string,
  accessToken: string,
): Promise<boolean> => {
  try {
    await Keychain.setGenericPassword(login, accessToken, {
      service: KEYCHAIN_SERVICE,
    });
    return true;
  } catch (error) {
    console.error('Error storing credentials:', error);
    return false;
  }
};

/**
 * Retrieve stored credentials from Keychain
 */
export const getStoredCredentials = async (): Promise<AuthCredentials | null> => {
  try {
    const credentials = await Keychain.getGenericPassword({
      service: KEYCHAIN_SERVICE,
    });
    if (credentials) {
      return {
        login: credentials.username,
        accessToken: credentials.password,
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting credentials:', error);
    return null;
  }
};

/**
 * Clear stored credentials from Keychain
 */
export const clearCredentials = async (): Promise<boolean> => {
  try {
    await Keychain.resetGenericPassword({ service: KEYCHAIN_SERVICE });
    return true;
  } catch (error) {
    console.error('Error clearing credentials:', error);
    return false;
  }
};

// ============================================
// First Login Flag (for skipping onboarding)
// ============================================

/**
 * Check if user has logged in before (skip onboarding for returning users)
 * Uses Keychain so the flag persists even after app uninstall (only reset on device wipe)
 */
export const hasLoggedInBefore = async (): Promise<boolean> => {
  try {
    const result = await Keychain.getGenericPassword({
      service: FIRST_LOGIN_FLAG_SERVICE,
    });
    return result !== false;
  } catch (error) {
    console.error('Error checking first login flag:', error);
    return false;
  }
};

/**
 * Set the first login flag (call after successful login)
 * Uses Keychain so the flag persists even after app uninstall
 */
export const setHasLoggedInBefore = async (): Promise<void> => {
  try {
    await Keychain.setGenericPassword('logged', 'true', {
      service: FIRST_LOGIN_FLAG_SERVICE,
    });
  } catch (error) {
    console.error('Error setting first login flag:', error);
  }
};

// ============================================
// Terms Acceptance Flag
// ============================================

/**
 * Check if user has accepted terms before
 * Uses Keychain so the flag persists even after app uninstall
 */
export const hasAcceptedTerms = async (): Promise<boolean> => {
  try {
    const result = await Keychain.getGenericPassword({
      service: TERMS_ACCEPTED_SERVICE,
    });
    return result !== false;
  } catch (error) {
    console.error('Error checking terms accepted flag:', error);
    return false;
  }
};

/**
 * Set the terms accepted flag (call after first successful login with terms agreed)
 * Uses Keychain so the flag persists even after app uninstall
 */
export const setHasAcceptedTerms = async (): Promise<void> => {
  try {
    await Keychain.setGenericPassword('accepted', 'true', {
      service: TERMS_ACCEPTED_SERVICE,
    });
  } catch (error) {
    console.error('Error setting terms accepted flag:', error);
  }
};

/**
 * Login with username and password
 * 1. Call backend API to authenticate
 * 2. Store JWT in Keychain
 * 3. Sign in to Firebase with custom token (if available)
 */
export const login = async (
  loginName: string,
  password: string,
): Promise<AuthResult> => {
  // Call backend API (v2)
  const response: V2LoginResponse = await v2Login(loginName, password);

  // Store JWT in Keychain
  await storeCredentials(response.user.login, response.access_token);

  // Set the auth token for future API calls
  setAuthToken(response.access_token);

  // Sign in to Firebase if token is available
  let firebaseUser: FirebaseAuthTypes.User | null = null;
  if (response.firebase_token) {
    console.log('Firebase token received, attempting sign-in...');
    try {
      // Sign out existing Firebase session first (handles account switching)
      // This ensures we get a fresh session with the new user's token
      if (auth().currentUser) {
        console.log('Signing out existing Firebase session before new sign-in');
        await auth().signOut();
      }

      const userCredential = await auth().signInWithCustomToken(
        response.firebase_token,
      );
      firebaseUser = userCredential.user;
      console.log('Firebase sign-in successful, UID:', firebaseUser?.uid);
    } catch (firebaseError) {
      console.warn('Firebase sign-in failed:', firebaseError);
      // Continue without Firebase - JWT auth still works
    }
  } else {
    console.warn('No Firebase token received from backend - Firebase auth skipped');
  }

  // Log analytics events
  await analyticsService.logLogin('password');
  await analyticsService.setUserId(response.user.id);

  // Mark that user has logged in (for skipping onboarding next time)
  await setHasLoggedInBefore();

  return {
    user: response.user,
    firebaseUser,
  };
};

/**
 * Logout
 * 1. Clear Keychain credentials
 * 2. Clear auth token
 * 3. Sign out of Firebase
 * 4. Clear biometric credentials
 */
export const logout = async (): Promise<void> => {
  // Clear analytics user ID
  await analyticsService.setUserId(null);

  // Clear stored credentials (regular keychain only)
  await clearCredentials();

  // Clear auth token
  setAuthToken(null);

  // Sign out of Firebase
  try {
    if (auth().currentUser) {
      await auth().signOut();
      console.log('Firebase sign-out successful');
    }
  } catch (error) {
    console.warn('Firebase sign-out failed:', error);
  }

  // Clear biometric credentials
  await biometrics.clearBiometricCredentials();
};

/**
 * Try to restore session from stored credentials
 * Returns true if session was restored, false otherwise
 */
export const restoreSession = async (): Promise<AuthResult | null> => {
  const credentials = await getStoredCredentials();
  if (!credentials) {
    return null;
  }

  // Set the auth token
  setAuthToken(credentials.accessToken);

  try {
    // Verify token is still valid by calling /me endpoint (v2)
    const user = await v2GetMe();

    // Get current Firebase user (if any)
    const firebaseUser = auth().currentUser;

    return {
      user,
      firebaseUser,
    };
  } catch (error) {
    // Token is invalid, clear credentials
    console.warn('Session restoration failed:', error);
    await clearCredentials();
    setAuthToken(null);
    return null;
  }
};

/**
 * Check if user is currently authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const credentials = await getStoredCredentials();
  return credentials !== null;
};

/**
 * Get current Firebase user
 */
export const getCurrentFirebaseUser = (): FirebaseAuthTypes.User | null => {
  return auth().currentUser;
};

/**
 * Subscribe to Firebase auth state changes
 */
export const onAuthStateChanged = (
  callback: (user: FirebaseAuthTypes.User | null) => void,
): (() => void) => {
  return auth().onAuthStateChanged(callback);
};

// ============================================
// Biometric Authentication Functions
// ============================================

/**
 * Check if biometrics are supported on this device
 */
export const isBiometricsSupported = async (): Promise<boolean> => {
  return biometrics.isBiometricsSupported();
};

/**
 * Get the supported biometry type
 */
export const getSupportedBiometryType = async (): Promise<biometrics.BiometryType> => {
  return biometrics.getSupportedBiometryType();
};

/**
 * Check if biometric login is enabled
 */
export const isBiometricLoginEnabled = async (): Promise<boolean> => {
  return biometrics.hasBiometricCredentials();
};

/**
 * Enable biometric login for the current session
 * Call this after a successful password login
 */
export const enableBiometricLogin = async (): Promise<boolean> => {
  const credentials = await getStoredCredentials();
  if (!credentials) {
    console.warn('No credentials to enable biometric login');
    return false;
  }

  return biometrics.enableBiometrics(credentials.login, credentials.accessToken);
};

/**
 * Disable biometric login
 */
export const disableBiometricLogin = async (): Promise<boolean> => {
  return biometrics.disableBiometrics();
};

/**
 * Login using biometric authentication
 * Uses Firebase session persistence to get a fresh ID token
 */
export const loginWithBiometrics = async (): Promise<AuthResult | null> => {
  // First, verify biometrics are enabled (this triggers the biometric prompt)
  const biometricCredentials = await biometrics.getBiometricCredentials();
  if (!biometricCredentials) {
    console.log('Biometric authentication cancelled or failed');
    return null;
  }

  // Check if Firebase user session is still valid
  const firebaseUser = auth().currentUser;
  if (!firebaseUser) {
    console.warn('Biometric login failed - no Firebase session');
    // Don't clear biometric credentials - user just needs to password login once
    // to restore Firebase session, then biometrics will work again
    return null;
  }

  try {
    // Get fresh Firebase ID token (Firebase handles refresh automatically)
    const idToken = await firebaseUser.getIdToken(true);
    console.log('Got fresh Firebase ID token for biometric login');

    // Set the Firebase ID token for API calls
    setAuthToken(idToken);

    // Call /me endpoint to get user info (v2)
    const user = await v2GetMe();

    // Store the token in keychain for session persistence
    await storeCredentials(user.login, idToken);

    // Log analytics
    await analyticsService.logLogin('biometric');
    await analyticsService.setUserId(user.id);

    return {
      user,
      firebaseUser,
    };
  } catch (error) {
    console.warn('Biometric login failed:', error);
    // Don't clear biometric credentials on API error - Firebase session might still be valid
    setAuthToken(null);
    return null;
  }
};

/**
 * Update biometric credentials with new token
 * Call this when token is refreshed
 */
export const updateBiometricCredentials = async (
  login: string,
  accessToken: string,
): Promise<boolean> => {
  const hasBiometrics = await biometrics.hasBiometricCredentials();
  if (hasBiometrics) {
    return biometrics.storeBiometricCredentials(login, accessToken);
  }
  return false;
};

